import type { LabelKind, OrgTenantTag } from '@onefootprint/request-types/dashboard';
import { DateRangeInput, Form, TextInput } from '@onefootprint/ui';
import { useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useEntitiesQueryParams from '@/components/entities/hooks/use-entities-query-params';
import FiltersDrawer from '@/components/filters-drawer';
import useSession from '@/hooks/use-session';
import PlaybookList from './components/playbooks';
import TagsList from './components/tags-list';
import { FiltersDateRange, type FormData } from './drawer-filter.type';
import useDateOptions from './hooks/use-date-options';
import useInitialFilters from './hooks/use-initial-filters';
import transformFormDataToQuery from './utils/transform-form-to-query';
type DrawerFilterProps = {
  tags: OrgTenantTag[];
};
const DrawerFilter = ({
  tags
}: DrawerFilterProps) => {
  const {
    t
  } = useTranslation('common', {
    keyPrefix: 'pages.entities.filters.drawer'
  });
  const {
    data: {
      user
    }
  } = useSession();
  const [open, setOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const options = useDateOptions();
  const queryParams = useEntitiesQueryParams();
  const {
    filtersCount,
    hasFilters
  } = queryParams;
  const {
    defaultValues,
    initialValues
  } = useInitialFilters();
  const methods = useForm<FormData>({
    defaultValues
  });
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch
  } = methods;
  const [period, labels = [], selectedTags = [], others = [], playbooks, playbookVersions] = watch(['period', 'labels', 'tags', 'others', 'playbooks', 'playbookVersions']);
  useEffect(() => {
    if (period === FiltersDateRange.Custom) {
      setIsDatePickerOpen(true);
    } else {
      setIsDatePickerOpen(false);
    }
  }, [period]);
  const drawerRef = useRef<HTMLDivElement>(null);
  const playbooksCount = [playbooks, playbookVersions].flatMap(Object.values).filter(Boolean).length;
  const getOtherOptions = () => {
    const options_0 = [{
      label: t($ => $.other.onWatchlist),
      value: 'watchlistHit'
    }, {
      label: t($_0 => $_0.other.hasOutstandingWorkflowRequest),
      value: 'hasOutstandingWorkflowRequest'
    }];
    if (user?.isFirmEmployee) {
      options_0.push({
        label: t($_1 => $_1.other.showUnverified),
        value: 'showUnverified'
      });
    }
    return options_0;
  };
  const handleOpenChange = (nextOpen: boolean) => {
    reset(defaultValues);
    setOpen(nextOpen);
  };
  const onSubmit = (formData: FormData) => {
    queryParams.push({
      ...transformFormDataToQuery(formData),
      cursor: undefined
    });
    handleOpenChange(false);
  };
  const handleReset = () => {
    reset(initialValues);
    queryParams.push({
      dateRangeEnd: undefined,
      dateRangeStart: undefined,
      watchlistHit: undefined,
      hasOutstandingWorkflowRequest: undefined,
      showUnverified: undefined,
      labels: undefined,
      tags: undefined,
      playbookIds: undefined,
      externalId: undefined
    });
    handleOpenChange(false);
  };
  const labelValues: Record<string, LabelKind> = {
    active: 'active',
    fraud: 'offboard_fraud',
    other: 'offboard_other'
  };
  return <FiltersDrawer count={hasFilters ? filtersCount : 0} open={open} onOpenChange={handleOpenChange} onApply={handleSubmit(onSubmit)} onClear={handleReset}>
      <div ref={drawerRef}>
        <FormProvider {...methods}>
          <form className="flex flex-col w-full" onSubmit={handleSubmit(onSubmit)}>
            <div className="pb-5">
              <TextInput size="compact" label={t($_2 => $_2.other.externalId)} placeholder={t($_3 => $_3.other.externalIdPlaceholder)} {...register('externalId')} autoCapitalize="off" autoComplete="off" autoCorrect="off" />
            </div>
            <FiltersDrawer.Section title={t($_4 => $_4.created.label)} count={period === FiltersDateRange.AllTime ? 0 : 1} defaultOpen>
              <div className="flex flex-col gap-3">
                <Controller control={control} name="period" render={({
                field
              }) => <FiltersDrawer.Chips aria-label={t($_5 => $_5.created.label)} options={options} value={field.value} onChange={field.onChange} />} />
                {isDatePickerOpen && <Controller control={control} name="customDate" render={({
                field: field_0
              }) => <DateRangeInput startDate={field_0.value.from} endDate={field_0.value.to} portalContainer={drawerRef.current} collisionBoundary={drawerRef.current} side="top" onChange={(nextStartDate?: Date, nextEndDate?: Date) => {
                field_0.onChange({
                  from: nextStartDate,
                  to: nextEndDate
                });
              }} onOpenChange={open_0 => setIsDatePickerOpen(open_0)} />} />}
              </div>
            </FiltersDrawer.Section>
            <FiltersDrawer.Section title={t($_6 => $_6.labels.label)} count={labels.length} defaultOpen>
              <Form.CheckboxGroup<FormData> name="labels" options={[{
              label: t($_7 => $_7.labels.options.active),
              value: labelValues.active
            }, {
              label: t($_8 => $_8.labels.options.fraud),
              value: labelValues.fraud
            }, {
              label: t($_9 => $_9.labels.options.other),
              value: labelValues.other
            }]} className="flex flex-col gap-2" />
            </FiltersDrawer.Section>
            {tags.length ? <FiltersDrawer.Section title={t($_10 => $_10.tags.label)} count={selectedTags.length}>
                <TagsList tags={tags} />
              </FiltersDrawer.Section> : null}
            <FiltersDrawer.Section title={t($_11 => $_11.playbooks.label)} count={playbooksCount}>
              <PlaybookList />
            </FiltersDrawer.Section>
            <FiltersDrawer.Section title={t($_12 => $_12.other.label)} count={others.length}>
              <Form.CheckboxGroup<FormData> name="others" options={getOtherOptions()} className="flex flex-col gap-2" />
            </FiltersDrawer.Section>
          </form>
        </FormProvider>
      </div>
    </FiltersDrawer>;
};
export default DrawerFilter;